import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";
import { isElevated } from "@/contexts/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { fmtEvento } from "@/pages/calendario/calendarioComuni";

// Sprint CRM-2B / CRM-2C — campanella unica: richieste di cancellazione (solo a chi le vede oggi), attività, notifiche personali
export default function CampanellaUnica({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [richieste, setRichieste] = useState(0);
  const [dati, setDati] = useState({ attivita: [], avvisi: [] });
  const gestisce = isElevated(user);
  useEffect(() => {
    if (!user) return undefined;
    const carica = () => {
      if (gestisce) api.get("/deletion-requests").then((r) => setRichieste((r.data || []).length)).catch(() => setRichieste(0));
      api.get("/notifiche/campanella").then((r) => setDati({ attivita: r.data?.attivita || [], avvisi: r.data?.notifiche || [] }))
        .catch(() => setDati({ attivita: [], avvisi: [] }));
    };
    carica();
    const t = setInterval(carica, 60000);
    window.addEventListener("richieste-cancellazione-aggiornate", carica);
    return () => { clearInterval(t); window.removeEventListener("richieste-cancellazione-aggiornate", carica); };
  }, [user, gestisce, location.pathname]);
  if (!user) return null;
  const n = (gestisce ? richieste : 0) + dati.attivita.length + dati.avvisi.length;
  const apriAvviso = async (a) => {
    await api.post(`/notifiche/${a.id}/letto`).catch(() => {});
    setDati((d) => ({ ...d, avvisi: d.avvisi.filter((x) => x.id !== a.id) }));
    navigate(a.collegamento);
  };
  const tuttiLetti = async () => { await api.post("/notifiche/letti-tutti").catch(() => {}); setDati((d) => ({ ...d, avvisi: [] })); };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" title="Avvisi" aria-label={`Avvisi: ${n} in attesa`}
          className={`relative h-9 w-9 inline-flex items-center justify-center rounded-md border transition-colors ${
            n > 0 ? "text-rose-600 bg-rose-50 border-rose-200 hover:bg-rose-100" : "text-slate-400 bg-white border-slate-200 hover:bg-slate-50"}`}
          data-testid="campanella-richieste">
          <Bell className="h-[18px] w-[18px]" />
          {n > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-600 text-white text-[10px] leading-4"
            data-testid="campanella-richieste-count">{n}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80" data-testid="campanella-menu">
        {gestisce && <>
          <DropdownMenuLabel>Richieste di cancellazione</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => navigate("/admin/deletion-requests")} data-testid="campanella-sezione-richieste">{richieste} in attesa</DropdownMenuItem>
          <DropdownMenuSeparator /></>}
        <DropdownMenuLabel>Attività</DropdownMenuLabel>
        {!dati.attivita.length && <div className="px-2 py-1 text-xs text-slate-400">Nessuna attività di oggi o scaduta</div>}
        {dati.attivita.map((e) => <DropdownMenuItem key={e.id} onClick={() => navigate(`/calendario?evento=${e.id}`)} data-testid={`campanella-attivita-${e.id}`}>
          <span className="font-mono text-xs mr-2">{fmtEvento(e)}</span>{e.titolo}</DropdownMenuItem>)}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="flex justify-between">Notifiche
          {!!dati.avvisi.length && <button type="button" className="text-xs text-blue-600 font-normal" onClick={tuttiLetti} data-testid="campanella-tutti-letti">Segna tutti come letti</button>}</DropdownMenuLabel>
        {!dati.avvisi.length && <div className="px-2 py-1 text-xs text-slate-400">Nessuna notifica</div>}
        {dati.avvisi.map((a) => <DropdownMenuItem key={a.id} onClick={() => apriAvviso(a)} data-testid={`campanella-avviso-${a.id}`}>
          <span className="flex-1">{a.testo}</span>
          {/* Sprint CRM-3 — pratica annullata: apre la finestra «Persa» della scheda opportunità */}
          {a.tipo === "crm_pratica_annullata" && <span className="ml-2 rounded border border-rose-300 px-2 py-0.5 text-xs text-rose-700"
            data-testid={`campanella-segna-persa-${a.id}`}>Segna persa</span>}</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
